<template>
    <div class="profile">
        <base-form class="profile__form display--grid">
            <base-card
                class="card--identification"
            >
                <template #card__title >
                    <span class="capitalize display--block">
                        {{ $t(`global.identification`) }}
                    </span>
                </template>
                <template #card__body >
                    <div class="profile__body display--grid">

                        <base-field
                            class="profile__body__name"
                            :settings="{
                                label: { class: 'capitalize', text: $t('global.name') },
                                input: {
                                    component: 'InputBasic',
                                    id: 'name',
                                    name: 'name',
                                    type: 'text',
                                    class: 'field__input--default typography--default color--default',
                                    value: model.name,
                                    browserAutocomplete: 'name',
                                    isRequired: $route.meta.isAdd,
                                    isMissing: errors.missing.name,
                                    errorOnSubmit: {
                                        show: errors.api.name ? true : false,
                                        messages: errors.api.name
                                    }
                                },
                                isEdition,
                                reset,
                                allowFilling: true
                            }"
                        />
                        <base-field
                            class="profile__body__email"
                            :settings="{
                                label: { class: 'capitalize', text: $t('global.email') },
                                input: {
                                    component: 'InputBasic',
                                    id: 'email',
                                    name: 'email',
                                    type: 'text',
                                    class: 'field__input--default typography--default color--default',
                                    value: model.email,
                                    browserAutocomplete: 'email',
                                    fileValidation: 'basicEmail',
                                    errors: [{
                                        type: 'email', warning: $t('error.email')
                                    }],
                                    errorOnSubmit: {
                                        show: errors.api.email ? true : false,
                                        messages: errors.api.email
                                    }
                                },
                                isEdition,
                                reset,
                                allowFilling: true
                            }"
                        />
                    </div>
                </template>
            </base-card>
            <base-card
                class="card--password-change"
            >
                <template #card__title >
                    <span class="capitalize display--block">
                        {{ $t(`global.passwordChange`) }}
                    </span>
                </template>
                <template #card__body >
                    <div class="profile__body display--grid">
                        <base-password
                            :input="input"
                            class="profile__body__password"
                            :settings="{
                                label: { class: 'capitalize', text: $t('global.newPassword') },
                                input: {
                                    name: 'password',
                                    class: 'field__input--default typography--default color--default',
                                    placeholder: $t('global.password'),
                                    browserAutocomplete: 'new-password',
                                    fileValidation: 'password',
                                    errors: [{
                                        type: 'validateStrenghtPassword', warning: $t('error.password')
                                    }],
                                    isRequired: false,
                                    isMissing: errors.missing.password,
                                    noListener: true
                                }
                            }"
                        />
                        <base-password
                            :input="input"
                            class="profile__body__password-confirmation"
                            :settings="{
                                label: { class: 'capitalize', text: $t('global.confirmPassword') },
                                input: {
                                    name: 'confirmPassword',
                                    class: 'field__input--default typography--default color--default',
                                    placeholder: $t('global.confirmPassword'),
                                    browserAutocomplete: 'confirm-password',
                                    fileValidation: 'confirmPassword',
                                    errors: [{
                                        type: 'sameAsPassword', warning: $t('error.confirmPassword')
                                    }],
                                    validationParameter: payload.password,
                                    isRequired: payload.hasOwnProperty( 'password' ),
                                    isMissing: errors.missing.confirmPassword,
                                    noListener: true
                                }
                            }"
                        />
                    </div>
                </template>
            </base-card>
            <div class="profile__body__actions display--grid">
                <base-button
                    @click.prevent.native="logout"
                    class="profile__body__actions__logout background--white display--flex"
                    :hasHover="false"
                    :customClass="true"
                >
                    <template #button__icon>
                        <icon-logout class="profile__body__actions__logout__icon"/>
                    </template>
                    <template #button__text>
                        <span class="profile__body__actions__logout__text capitalize display--inline-b">
                            {{ $t( 'authentication.logout' ) }}
                        </span>
                    </template>
                </base-button>

                <submission-handler v-if="getters.payloadHaveKeys">
                    <base-button
                        type="submit"
                        class="profile__body__actions__save background--blue color--white display--block"
                        v-html="$t('global.save')"
                    />
                </submission-handler>

                <base-button
                    v-if="getters.payloadHaveKeys"
                    class="profile__body__actions__cancel background--transparent color--blue"
                    @click="cancelSubmission"
                    v-html="$t('global.cancel')"
                />
            </div>
        </base-form>
    </div>
</template>

<script>
import { watch } from '@vue/composition-api';

// hooks
import { singleStructure } from '@/hooks/use-data';

// store
// eslint-disable-next-line
import { storePayload, useStore, mapState } from '@/hooks/use-store';

// models
// eslint-disable-next-line
import { toggleRequiredFields } from '@/models/helpers/toggle-required-fields';

export default {
    components: {
        ...singleStructure.components,
        BasePassword: () => import( /* webpackChunkName: 'BasePassword' */ '@/components/bundles/fields/BasePassword' ),
        IconLogout: () => import( /* webpackChunkName: 'IconLogout' */ '@/components/bundles/layout/images/icons/IconLogout' ),
        SubmissionHandler: () => import(
            /* webpackChunkName: 'SubmissionHandler' */
            '@/components/bundles/layout/SubmissionHandler/SubmissionHandler'
        )
    },
    props: {
        ...singleStructure.props,
        input: Function
    },
    setup ( _, { root: { $route, $router } }) {
        const { actions, getters } = useStore();

        // obtenção da store errors & reset
        const { errors, reset } = storePayload();

        // obtenção do payload
        const payload = mapState({ module: 'storePayload', key: 'payload' });

        // logout
        const logout = () => {
            actions( 'logout' );
        };

        // o utilizador cancelou a submissão.
        // é fechado a barra de acção e dado clear
        // ao payload.
        const cancelSubmission = () => {
            ( $route.meta.isAdd )
                ? $router.go( -1 ) // vai para a página anterior
                : actions( 'resetModel' );
        };

        // TODO: watch payload.password. Se tiver password no payload, meter como obrigatório o confirmPassword.
        // TODO: Caso apague o password, remove obrigatoriedadde do confirmPassword.
        watch( () => payload.value.password,
            newPassword => {
                toggleRequiredFields( 'user', {
                    field: 'confirmPassword',
                    isRequired: true,
                    isRequiredOnEdit: newPassword !== undefined
                });
            }, {
                deep: false
            });

        return {
            errors,
            reset,
            payload,
            logout,
            cancelSubmission,
            getters
        };
    }
};
</script>

<style lang="scss">
    .profile {
        max-width: 1350px;
        margin: 0 auto;
        @include responsive( max-width, 1450px ) {
           margin: 0 45px;
        }
        &__form {
            grid-gap: 30px;
            grid-template: auto / repeat( 2, 1fr );
        }
        &__body {
            grid-template: auto / repeat( 2, 1fr );
            &__actions {
                &__logout {
                    @include rounded-corner( 10px );
                    padding: 9px 13px;
                    align-items: center;
                    &__text {
                        margin-left: 17px;
                        color: $color-grey;
                    }
                    @include transition( all .3s ease-in-out );
                    &:hover {
                        .profile__body__actions__logout__icon {
                            rect {
                                fill: $color-blue;
                            }
                            path {
                                fill: $color-white;
                            }
                        }
                        .profile__body__actions__logout__text {
                            color: $color-black;
                        }
                    }
                }
            }
            &__actions {
                grid-column: 1 / 2;
                grid-template: auto / repeat( 3, 1fr );
            }
        }
    }
</style>